import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BookmarkPlus } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AppContext';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';

export default function SaveAsTemplateModal({ campaignId, campaignVertical, onClose, onSaved }) {
  const { user } = useAuth();
  const saveAsTemplate = useWorkflowStore((state) => state.saveAsTemplate);

  const [mode, setMode] = useState('new'); // 'new' | 'overwrite'
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState(campaignVertical || '');
  const [description, setDescription] = useState('');
  const [verticals, setVerticals] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/config/verticals')
      .then((res) => setVerticals(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVerticals([]));
  }, []);

  useEffect(() => {
    if (mode !== 'overwrite') return;
    setLoadingTemplates(true);
    api.get('/flow-templates/', { params: { limit: 100, page_size: 100, per_page: 100 } })
      .then((res) => {
        const items = res.data?.items || res.data || [];
        // Default to empty, not the unfiltered list, if we don't know who
        // "mine" is yet - this dropdown exists to scope destructive
        // overwrites to the current user's own templates.
        setMyTemplates(user?.id ? items.filter((t) => t.created_by_id === user.id) : []);
      })
      .catch(() => setMyTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, [mode, user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'new' && !name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (mode === 'overwrite' && !templateId) {
      toast.error('Select a template to overwrite');
      return;
    }

    setSaving(true);
    try {
      const payload =
        mode === 'new'
          ? { name: name.trim(), vertical, description: description.trim() || undefined }
          : { template_id: Number(templateId) };

      await saveAsTemplate(campaignId, payload);
      toast.success(mode === 'new' ? 'Saved current flow as a new template' : 'Template overwritten with current flow');
      onSaved?.();
      onClose();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You don't have permission to save flow templates.");
      } else {
        const detail = err.response?.data?.detail || err.response?.data?.message || 'Failed to save template';
        toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookmarkPlus size={18} /> Save as Template
          </h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                className={`btn btn-sm ${mode === 'new' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setMode('new')}
              >
                New Template
              </button>
              <button
                type="button"
                className={`btn btn-sm ${mode === 'overwrite' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setMode('overwrite')}
              >
                Overwrite Existing
              </button>
            </div>

            {mode === 'new' ? (
              <>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Vertical</label>
                  <select className="select" value={vertical} onChange={(e) => setVertical(e.target.value)} required>
                    <option value="">— Select Vertical —</option>
                    {verticals.map((v) => (
                      <option key={v.identifier} value={v.identifier}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label className="form-label">Template to overwrite</label>
                {loadingTemplates ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading your templates…</div>
                ) : myTemplates.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    You don't have any templates to overwrite yet.
                  </div>
                ) : (
                  <select className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)} required>
                    <option value="">— Select Template —</option>
                    {myTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.vertical})</option>
                    ))}
                  </select>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Only the template's graph is replaced — its name, vertical, and description stay the same.
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <div className="spinner" /> : mode === 'new' ? 'Save as new template' : 'Overwrite template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
